/* ==========================================================================
   GEEKZ STORE — Retro Interactive FX (Level 3)
   Konami Code, Achievement Toasts, Arcade SFX, CRT Power-On Reveal
   All 80s vintage vibes. Pure vanilla JS + Web Audio API.
   ========================================================================== */

(function () {
    "use strict";

    /* Bail if user prefers reduced motion */
    if (window.matchMedia("(prefers-reduced-motion: reduce)").matches) return;

    /* ========================================================================
     1. WEB AUDIO — Retro Chiptune Sound Engine
     Generates 8-bit bleeps/bloops using oscillators. No audio files needed.
     ======================================================================== */

    /** @type {AudioContext|null} */
    var audioCtx = null;
    var sfxEnabled = false;

    function getAudioCtx() {
        if (!audioCtx) {
            audioCtx = new (
                window.AudioContext ||
                /** @type {any} */ (window).webkitAudioContext
            )();
        }
        return audioCtx;
    }

    /**
     * Play a retro bleep.
     * @param {number} freq   - Frequency in Hz (261=C4, 523=C5, 784=G5)
     * @param {number} dur    - Duration in seconds
     * @param {string} type   - Oscillator wave: 'square','triangle','sawtooth'
     * @param {number} volume - 0-1
     */
    function bleep(freq, dur, type, volume) {
        if (!sfxEnabled) return;
        try {
            var ctx = getAudioCtx();
            var osc = ctx.createOscillator();
            var gain = ctx.createGain();
            osc.type = /** @type {OscillatorType} */ (type || "square");
            osc.frequency.value = freq;
            gain.gain.setValueAtTime(volume || 0.08, ctx.currentTime);
            gain.gain.exponentialRampToValueAtTime(
                0.001,
                ctx.currentTime + dur,
            );
            osc.connect(gain);
            gain.connect(ctx.destination);
            osc.start(ctx.currentTime);
            osc.stop(ctx.currentTime + dur);
        } catch (e) {
            /* silent fail — no audio support */
        }
    }

    /* Pre-defined sound presets */
    var sounds = {
        hover: function () {
            bleep(660, 0.06, "square", 0.05);
        },
        click: function () {
            bleep(880, 0.1, "square", 0.07);
        },
        success: function () {
            bleep(523, 0.12, "square", 0.07);
            setTimeout(function () {
                bleep(659, 0.12, "square", 0.07);
            }, 80);
            setTimeout(function () {
                bleep(784, 0.18, "square", 0.08);
            }, 160);
        },
        achievement: function () {
            bleep(523, 0.1, "triangle", 0.08);
            setTimeout(function () {
                bleep(659, 0.1, "triangle", 0.08);
            }, 100);
            setTimeout(function () {
                bleep(784, 0.1, "triangle", 0.08);
            }, 200);
            setTimeout(function () {
                bleep(1047, 0.25, "triangle", 0.1);
            }, 300);
        },
        konami: function () {
            var notes = [523, 523, 784, 784, 659, 659, 523, 659, 784, 1047];
            notes.forEach(function (n, i) {
                setTimeout(function () {
                    bleep(n, 0.15, "square", 0.09);
                }, i * 120);
            });
        },
        powerOn: function () {
            bleep(120, 0.4, "sawtooth", 0.06);
            setTimeout(function () {
                bleep(240, 0.3, "triangle", 0.05);
            }, 200);
            setTimeout(function () {
                bleep(480, 0.2, "triangle", 0.04);
            }, 350);
        },
        addToCart: function () {
            bleep(440, 0.08, "square", 0.06);
            setTimeout(function () {
                bleep(554, 0.08, "square", 0.06);
            }, 70);
            setTimeout(function () {
                bleep(659, 0.15, "triangle", 0.08);
            }, 140);
        },
    };

    /* ========================================================================
     2. SOUND TOGGLE BUTTON
     Inject a small speaker icon into the header right column
     ======================================================================== */

    function initSoundToggle() {
        /* Check localStorage for saved preference */
        sfxEnabled = localStorage.getItem("geekz-sfx") === "on";

        var btn = document.createElement("button");
        btn.className =
            "geekz-sfx-toggle" + (sfxEnabled ? "" : " is-muted");
        btn.setAttribute("aria-label", "Toggle retro sound effects");
        btn.setAttribute("type", "button");
        btn.innerHTML =
            '<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">' +
            '<polygon points="11 5 6 9 2 9 2 15 6 15 11 19 11 5"/>' +
            '<path class="geekz-sfx-waves" d="M15.54 8.46a5 5 0 0 1 0 7.07M19.07 4.93a10 10 0 0 1 0 14.14"/>' +
            "</svg>";

        btn.addEventListener("click", function () {
            sfxEnabled = !sfxEnabled;
            localStorage.setItem("geekz-sfx", sfxEnabled ? "on" : "off");
            btn.classList.toggle("is-muted", !sfxEnabled);

            if (sfxEnabled) {
                /* Wake up AudioContext on user gesture (Chrome autoplay policy) */
                getAudioCtx();
                sounds.success();
            }
        });

        /* Insert inside <header-actions> as the very first child so it sits
           before account/cart icons with the same flex gap spacing */
        var headerActions = document.querySelector("header-actions");
        if (headerActions) {
            headerActions.insertBefore(btn, headerActions.firstChild);
        }
    }

    /* ========================================================================
     3. BUTTON/LINK HOVER SOUNDS
     Arcade bleeps on interactive element hover
     ======================================================================== */

    function initHoverSounds() {
        document.addEventListener(
            "mouseenter",
            function (e) {
                var target = /** @type {HTMLElement|null} */ (e.target);
                if (!target) return;
                var el = target.closest(
                    ".button, .button-secondary, .button-custom, .menu-list__link, .product-card a",
                );
                if (el) sounds.hover();
            },
            true,
        );

        document.addEventListener(
            "click",
            function (e) {
                var target = /** @type {HTMLElement|null} */ (e.target);
                if (!target) return;
                var el = target.closest(".button, .button-secondary, .button-custom");
                if (el) sounds.click();
            },
            true,
        );

        /* Cart add: listen for the theme's cart events */
        document.addEventListener("cart:item-added", function () {
            sounds.addToCart();
        });
    }

    /* ========================================================================
     4. ACHIEVEMENT TOASTS
     Xbox 360-style "Achievement Unlocked" popups
     ======================================================================== */

    /** @type {{icon:string,title:string}[]} */
    var achievementQueue = [];
    var achievementShowing = false;

    var achievements = {
        firstVisit: {
            icon: "🏆",
            title: "Welcome, Collector!",
            triggered: false,
            check: function () {
                return !localStorage.getItem("geekz-ach-visit");
            },
            onTrigger: function () {
                localStorage.setItem("geekz-ach-visit", "1");
            },
        },
        explorer: {
            icon: "🗺️",
            title: "Store Explorer",
            triggered: false,
            check: function () {
                if (localStorage.getItem("geekz-ach-explore")) return false;
                var viewed = JSON.parse(
                    sessionStorage.getItem("geekz-pages") || "[]",
                );
                return viewed.length >= 3;
            },
            onTrigger: function () {
                localStorage.setItem("geekz-ach-explore", "1");
            },
        },
        scrollMaster: {
            icon: "📜",
            title: "Scroll Master",
            triggered: false,
            check: function () {
                if (localStorage.getItem("geekz-ach-scroll")) return false;
                var scroller = pageScroller();
                var scrolled =
                    (scroller.scrollTop + scroller.clientHeight) /
                    scroller.scrollHeight;
                return scrolled > 0.9;
            },
            onTrigger: function () {
                localStorage.setItem("geekz-ach-scroll", "1");
            },
        },
        nightOwl: {
            icon: "🦉",
            title: "Night Owl Shopper",
            triggered: false,
            check: function () {
                if (localStorage.getItem("geekz-ach-night")) return false;
                var hour = new Date().getHours();
                return hour >= 23 || hour < 5;
            },
            onTrigger: function () {
                localStorage.setItem("geekz-ach-night", "1");
            },
        },
        cartStarter: {
            icon: "🛒",
            title: "Cart Loaded!",
            triggered: false,
            check: function () {
                return false;
            } /* triggered by event, not polling */,
            onTrigger: function () {},
        },
    };

    /* Create the toast DOM element (reused for all achievements) */
    /** @type {HTMLElement|null} */
    var toastEl = null;
    /** @type {ReturnType<typeof setTimeout>|null} */
    var toastTimeout = null;

    function createToast() {
        toastEl = document.createElement("div");
        toastEl.className = "geekz-achievement";
        toastEl.setAttribute("role", "status");
        toastEl.setAttribute("aria-live", "polite");
        toastEl.innerHTML =
            '<span class="geekz-achievement__icon"></span>' +
            '<div class="geekz-achievement__body">' +
            '  <span class="geekz-achievement__label">Achievement Unlocked</span>' +
            '  <span class="geekz-achievement__title"></span>' +
            "</div>";
        document.body.appendChild(toastEl);
    }

    /** @param {{icon:string,title:string}} ach */
    function showAchievement(ach) {
        if (!toastEl) createToast();

        achievementQueue.push(ach);
        if (achievementShowing) return;
        processQueue();
    }

    function processQueue() {
        if (achievementQueue.length === 0) {
            achievementShowing = false;
            return;
        }

        achievementShowing = true;
        var ach = achievementQueue.shift();
        if (!toastEl || !ach) {
            achievementShowing = false;
            return;
        }

        /** @type {HTMLElement} */ (
            toastEl.querySelector(".geekz-achievement__icon")
        ).textContent = ach.icon;
        /** @type {HTMLElement} */ (
            toastEl.querySelector(".geekz-achievement__title")
        ).textContent = ach.title;

        /* Slide in */
        var _toast = toastEl;
        requestAnimationFrame(function () {
            _toast.classList.add("is-visible");
        });

        sounds.achievement();

        /* Slide out after 4s */
        if (toastTimeout) clearTimeout(toastTimeout);
        toastTimeout = setTimeout(function () {
            _toast.classList.remove("is-visible");
            /* Process next after transition */
            setTimeout(processQueue, 600);
        }, 4000);
    }

    /** @param {string} key */
    function triggerAchievement(key) {
        var ach = achievements[/** @type {keyof typeof achievements} */ (key)];
        if (!ach || ach.triggered) return;
        ach.triggered = true;
        ach.onTrigger();
        showAchievement(ach);
    }

    /* Horizon 4.x scrolls inside .page-wrapper on desktop (990px and up);
       on smaller screens the document itself scrolls. */
    function pageScroller() {
        var wrapper = document.querySelector(".page-wrapper");
        if (wrapper && wrapper.scrollHeight > wrapper.clientHeight + 1) {
            var overflowY = getComputedStyle(wrapper).overflowY;
            if (overflowY === "auto" || overflowY === "scroll") return wrapper;
        }
        return document.scrollingElement || document.documentElement;
    }

    function initAchievements() {
        /* Track page views for "Explorer" achievement */
        var viewed = JSON.parse(sessionStorage.getItem("geekz-pages") || "[]");
        var path = window.location.pathname;
        if (viewed.indexOf(path) === -1) {
            viewed.push(path);
            sessionStorage.setItem("geekz-pages", JSON.stringify(viewed));
        }

        /* Check on load (delayed so page settles) */
        setTimeout(function () {
            if (achievements.firstVisit.check())
                triggerAchievement("firstVisit");

            setTimeout(function () {
                if (achievements.nightOwl.check())
                    triggerAchievement("nightOwl");
            }, 2000);

            setTimeout(function () {
                if (achievements.explorer.check())
                    triggerAchievement("explorer");
            }, 3000);
        }, 1500);

        /* Scroll-based check */
        /** @type {ReturnType<typeof setTimeout>|null} */
        var scrollThrottle = null;
        function onPageScroll() {
            if (scrollThrottle) return;
            scrollThrottle = setTimeout(function () {
                scrollThrottle = null;
                if (achievements.scrollMaster.check())
                    triggerAchievement("scrollMaster");
            }, 500);
        }
        window.addEventListener("scroll", onPageScroll, { passive: true });
        var pageWrapper = document.querySelector(".page-wrapper");
        if (pageWrapper)
            pageWrapper.addEventListener("scroll", onPageScroll, {
                passive: true,
            });

        /* Cart-based achievement */
        document.addEventListener("cart:item-added", function () {
            if (!achievements.cartStarter.triggered) {
                achievements.cartStarter.triggered = true;
                showAchievement(achievements.cartStarter);
            }
        });
    }

    /* ========================================================================
     5. KONAMI CODE EASTER EGG
     ↑ ↑ ↓ ↓ ← → ← → B A — triggers a retro celebration
     ======================================================================== */

    /** @type {number[]} */
    var konamiBuffer = [];
    var konamiCode = [38, 38, 40, 40, 37, 39, 37, 39, 66, 65]; // ArrowUp x2, ArrowDown x2, ArrowLeft, ArrowRight, ArrowLeft, ArrowRight, B, A
    /** @type {HTMLElement|null} */
    var konamiOverlay = null;

    function initKonami() {
        document.addEventListener("keydown", function (e) {
            konamiBuffer.push(e.keyCode);

            /* Only keep the last N keys */
            if (konamiBuffer.length > konamiCode.length) {
                konamiBuffer.shift();
            }

            /* Check match */
            if (konamiBuffer.length === konamiCode.length) {
                var match = konamiBuffer.every(function (val, i) {
                    return val === konamiCode[i];
                });

                if (match) {
                    konamiBuffer = [];
                    activateKonami();
                }
            }
        });
    }

    function activateKonami() {
        if (!konamiOverlay) {
            konamiOverlay = document.createElement("div");
            konamiOverlay.className = "geekz-konami-overlay";
            konamiOverlay.innerHTML =
                '<div class="geekz-konami-overlay__text">★ CHEAT CODE ACTIVATED ★</div>' +
                '<div class="geekz-konami-overlay__sub">You found the secret! True geek status confirmed.</div>';
            var _overlay = konamiOverlay;
            _overlay.addEventListener("click", function () {
                _overlay.classList.remove("is-active");
            });
            document.body.appendChild(_overlay);
        }

        sounds.konami();

        var _ko = /** @type {HTMLElement} */ (konamiOverlay);
        requestAnimationFrame(function () {
            _ko.classList.add("is-active");
        });

        /* Auto-dismiss after 5s */
        setTimeout(function () {
            _ko.classList.remove("is-active");
        }, 5000);

        /* Fire a special achievement */
        if (!localStorage.getItem("geekz-ach-konami")) {
            localStorage.setItem("geekz-ach-konami", "1");
            setTimeout(function () {
                showAchievement({ icon: "🎮", title: "Konami Master!" });
                sounds.achievement();
            }, 1500);
        }
    }

    /* ========================================================================
     6. CRT POWER-ON REVEAL (Enhanced)
     Every 3rd below-fold section gets the CRT expanding-line reveal
     instead of the standard fade-up. Adds the retro TV boot-up sound.
     ======================================================================== */

    function initCrtReveal() {
        /* Wait for scroll animations to tag sections with geekz-reveal */
        setTimeout(function () {
            var reveals = document.querySelectorAll(".geekz-reveal");
            reveals.forEach(function (el, i) {
                /* Every 3rd section gets the CRT power-on variant */
                if ((i + 1) % 3 === 0) {
                    el.classList.add("geekz-crt-on");
                }
            });

            /* Play power-on sound when CRT sections come into view */
            if (window.IntersectionObserver) {
                var crtSections = document.querySelectorAll(".geekz-crt-on");
                var crtObserver = new IntersectionObserver(
                    function (entries) {
                        entries.forEach(function (entry) {
                            if (entry.isIntersecting) {
                                sounds.powerOn();
                                crtObserver.unobserve(entry.target);
                            }
                        });
                    },
                    { threshold: 0.1 },
                );

                crtSections.forEach(function (el) {
                    crtObserver.observe(el);
                });
            }
        }, 300); /* Allow geekz-scroll-animations.js to tag elements first */
    }

    /* ========================================================================
     7. RETRO LOADING BAR
     Replace default page transition with 8-bit progress bar
     ======================================================================== */

    /** @type {HTMLElement|null} */
    var loadingBar = null;

    function initLoadingBar() {
        loadingBar = document.createElement("div");
        loadingBar.id = "geekz-loading-bar";
        loadingBar.style.cssText =
            "position:fixed;top:0;left:0;height:3px;width:0;" +
            "background:linear-gradient(90deg,#CA400F,#FFD700,#CA400F);" +
            "box-shadow:0 0 8px rgb(202 64 15 / 0.6),0 0 20px rgb(202 64 15 / 0.2);" +
            "z-index:999999;transition:width 0.3s ease;pointer-events:none;" +
            "border-radius:0 2px 2px 0;";
        document.body.appendChild(loadingBar);

        /* Start bar on link click (internal navigation) */
        document.addEventListener("click", function (e) {
            var target = /** @type {HTMLElement|null} */ (e.target);
            if (!target) return;
            var link = target.closest("a[href]");
            if (!link) return;

            var aLink = /** @type {HTMLAnchorElement} */ (link);
            var href = aLink.getAttribute("href");
            /* Skip external, hash, javascript, new-tab links */
            if (
                !href ||
                href.startsWith("#") ||
                href.startsWith("javascript") ||
                aLink.target === "_blank" ||
                aLink.hasAttribute("download")
            )
                return;

            /* Only same-origin */
            try {
                var url = new URL(href, window.location.origin);
                if (url.origin !== window.location.origin) return;
            } catch (err) {
                return;
            }

            if (!loadingBar) return;
            var _bar = loadingBar;
            /* Animate the bar */
            _bar.style.width = "0";
            _bar.style.opacity = "1";
            requestAnimationFrame(function () {
                _bar.style.width = "30%";
                setTimeout(function () {
                    _bar.style.width = "65%";
                }, 200);
                setTimeout(function () {
                    _bar.style.width = "85%";
                }, 600);
            });
        });

        /* Complete on page load (for back/forward navigation) */
        window.addEventListener("pageshow", function () {
            if (loadingBar && parseFloat(loadingBar.style.width) > 0) {
                var _b = loadingBar;
                _b.style.width = "100%";
                setTimeout(function () {
                    _b.style.opacity = "0";
                    setTimeout(function () {
                        _b.style.width = "0";
                    }, 300);
                }, 200);
            }
        });
    }

    /* ========================================================================
     BOOT — Initialize everything when DOM is ready
     ======================================================================== */

    function boot() {
        initSoundToggle();
        initHoverSounds();
        initAchievements();
        initKonami();
        initCrtReveal();
        initLoadingBar();
    }

    if (document.readyState === "loading") {
        document.addEventListener("DOMContentLoaded", boot);
    } else {
        boot();
    }
})();